import { History } from 'history';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import Popover from 'react-tiny-popover';
import { compose } from 'recompose';
import useReactRouter from 'use-react-router';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ADMIN } from '@mm/api-routes';
import { Line } from '@mm/components';
import classNames from 'classnames';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import { SIGNOUT } from '../../../common/constants/routes';
import { UserRoleContainer } from '../../../common/hooks/use-route-guard';
import { SettingsRoutes } from '../../../routes/settings-routes';
import { ImageWithFallback } from '../../../common/components/ImageWithFallback/ImageWithFallback';
import './UserDropdown.pcss';
import LogoutIcon from './UserDropdownIcons/Logout.svg';
import UserIcon from './UserDropdownIcons/User.svg';
import CompanyIcon from './UserDropdownIcons/Company.svg';
import BillingIcon from './UserDropdownIcons/Billing.svg';
import TeamIcon from './UserDropdownIcons/Team.svg';
import ReferralIcon from './UserDropdownIcons/Referral.svg';
import AdminIcon from './UserDropdownIcons/AdminSettings.svg';
import UserFallbackIcon from './UserDropdownIcons/UserFallback.svg';
import UserFallbackWhiteIcon from './UserDropdownIcons/UserFallbackWhite.svg';

type OuterProps = {
  onChange?: (isOpen: boolean) => void;
  isFreeTrialActive?: boolean;
  isMobile?: boolean;
};

type InnerProps = ReturnType<typeof mapStateToProps>;

type Props = OuterProps & InnerProps;

type DropdownItem = {
  name: string;
  icon?: string;
  link?: string;
  requiresAdmin?: boolean;
};

const logoutMenuItem: DropdownItem = {
  name: 'Logout',
  icon: LogoutIcon,
  link: SIGNOUT,
  requiresAdmin: false,
};

const myAccountMenuItem: DropdownItem = {
  name: 'My Account',
  icon: UserIcon,
  link: SettingsRoutes.myAccount.getLink(),
  requiresAdmin: false,
};

const dropdownItems: DropdownItem[] = [
  myAccountMenuItem,
  {
    name: 'Company',
    icon: CompanyIcon,
    link: SettingsRoutes.company.getLink(),
    requiresAdmin: true,
  },
  {
    name: 'Billing',
    icon: BillingIcon,
    link: SettingsRoutes.billing.getLink(),
    requiresAdmin: true,
  },
  {
    name: 'Team',
    icon: TeamIcon,
    link: SettingsRoutes.users.getLink(),
    requiresAdmin: false,
  },
  {
    name: 'Referrals',
    icon: ReferralIcon,
    link: SettingsRoutes.affiliates.getLink(),
    requiresAdmin: false,
  },
  logoutMenuItem,
];

type UserDropdownAreaProps = {
  admin: boolean;
  isCompanyAdmin: boolean;
  history: History;
  onItemClick?: () => void;
  isFreeTrialActive?: boolean;
  isMobile?: boolean;
};

const UserDropdownArea = ({
  admin,
  isCompanyAdmin,
  history,
  onItemClick,
  isFreeTrialActive,
  isMobile,
}: UserDropdownAreaProps) => {
  const {
    roleFlags: { isLiveChatAgent },
  } = UserRoleContainer.useContainer();

  const filteredRoles = isLiveChatAgent
    ? [myAccountMenuItem, logoutMenuItem]
    : dropdownItems.filter(({ requiresAdmin, name }) =>
        isMobile ? name !== 'Logout' : requiresAdmin ? isCompanyAdmin : true,
      );

  const onDropDownItemClick = (dropdownItem: DropdownItem) => {
    if (dropdownItem.link) {
      history.push(dropdownItem.link);
      if (onItemClick) {
        onItemClick();
      }
    }
  };

  return (
    <div
      className={classNames('user-dropdown__dropdown', {
        'user-dropdown__dropdown-with-trial': isFreeTrialActive,
        'user-dropdown__dropdown-mobile': isMobile,
      })}
    >
      {isMobile && (
        <div className="user-dropdown__dropdown-mobile-header">
          Personal Account
        </div>
      )}
      {admin && (
        <a
          className={classNames('user-dropdown__dropdown-item', {
            'user-dropdown__dropdown-item-mobile': isMobile,
          })}
          href={ADMIN}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={AdminIcon} className="user-dropdown__dropdown-item-icon" />
          Admin
        </a>
      )}

      {filteredRoles.map(dropdownItem => (
        <>
          {dropdownItem.name === 'Logout' && (
            <Line className="user-dropdown__dropdown-item-line" />
          )}
          <span
            className={classNames('user-dropdown__dropdown-item', {
              'user-dropdown__dropdown-item-mobile': isMobile,
            })}
            onClick={() => onDropDownItemClick(dropdownItem)}
            key={dropdownItem.name}
          >
            <img
              src={dropdownItem.icon}
              className="user-dropdown__dropdown-item-icon"
            />

            {dropdownItem.name}
          </span>
        </>
      ))}
    </div>
  );
};

const UserDropdown = (props: Props) => {
  const {
    userImage,
    session,
    company,
    onChange = () => {},
    isFreeTrialActive,
    isMobile,
  } = props;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const closeDropdown = () => setDropdownOpen(false);
  const isCompanyAdmin = !!(company && company.editable);
  const { history } = useReactRouter();
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);

  useEffect(() => {
    onChange(dropdownOpen);
  }, [dropdownOpen]);

  return (
    <div className="user-dropdown">
      <Popover
        isOpen={dropdownOpen}
        onClickOutside={closeDropdown}
        content={
          <UserDropdownArea
            admin={session.admin}
            isCompanyAdmin={isCompanyAdmin}
            history={history}
            onItemClick={closeDropdown}
            isFreeTrialActive={isFreeTrialActive}
            isMobile={isMobile}
          />
        }
        containerClassName="user-dropdown__popover-wrapper"
        containerStyle={{ overflow: '' }}
        position="right"
        align="end"
      >
        <div
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className={classNames('user-dropdown__link-area', {
            'user-dropdown__link-area-with-automations':
              isMobile || isAutomationsEnabled,
            'user-dropdown__link-area-selected--mobile':
              dropdownOpen && isMobile,
          })}
        >
          <ImageWithFallback
            src={userImage}
            fallbackImage={
              isMobile && dropdownOpen
                ? UserFallbackWhiteIcon
                : UserFallbackIcon
            }
            className={classNames(
              isAutomationsEnabled && !userImage
                ? 'user-dropdown__user-image--fallback'
                : 'user-dropdown__user-image',
              {
                'user-dropdown__user-image-non-fallback ':
                  (isAutomationsEnabled && !!userImage) || isMobile,
              },
            )}
            fallbackClassName="user-dropdown__user-image--fallback"
            alt="User Profile Image"
          />
        </div>
      </Popover>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
  userImage: state.session.session.image,
  session: state.session.session,
});

const enhance = compose<InnerProps, OuterProps>(connect(mapStateToProps));

export default enhance(UserDropdown);
